import { createContext, useContext, useRef } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useDragDismiss } from './drag.js';
import { useCover, useSheetLayer } from './OverlayHost.js';

export interface SheetTone {
  bg: string;
  fg: string;
  border: string;
  /** The grabber and the close button. */
  grip: string;
}

const DEFAULT_TONE: SheetTone = { bg: '#0b1016', fg: '#dfe9f2', border: 'rgba(255,255,255,.14)', grip: '#8fa4b5' };

const SheetContext = createContext<{ close: () => void }>({ close: () => undefined });

/** For a button inside a sheet that closes it: the sheet slides away first. */
export function useSheet(): { close: () => void } {
  return useContext(SheetContext);
}

/**
 * A sheet that slides up from the bottom, like an iPhone's.
 *
 * - A grabber at the top (36×5, in a 44-point strip). Pull the sheet DOWN from anywhere on it
 *   to put it away; on a list, once the list is at its top. It goes past a quarter of its
 *   height, or on a quick flick, and springs back otherwise. Pulling up stretches a little.
 * - The backdrop dims with the drag. Tapping it closes the sheet, unless the sheet is
 *   `critical` (then it needs its button or a long pull).
 * - Everything behind is covered, so nothing there can be tapped or scrolled.
 * - `onDismiss` is what closing means: some sheets close, some shrink to a bar at the top.
 */
export function BottomSheet({
  id,
  onDismiss,
  critical = false,
  size = 'auto',
  tone = DEFAULT_TONE,
  header,
  footer,
  closeLabel,
  label,
  zIndex = 60,
  children,
  bodyStyle,
}: {
  /** Names the cover, so a game underneath knows to pause. */
  id: string;
  onDismiss: () => void;
  critical?: boolean;
  size?: 'auto' | 'full';
  tone?: SheetTone;
  /** Pinned under the grabber; the sheet can be pulled by it. */
  header?: ReactNode;
  /** Pinned at the bottom, so the main button is never scrolled out of reach. */
  footer?: ReactNode;
  /** A small button beside the grabber, e.g. "HIDE" or "✕". */
  closeLabel?: string;
  label?: string;
  zIndex?: number;
  children?: ReactNode;
  bodyStyle?: CSSProperties;
}) {
  const layer = useSheetLayer();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const backdrop = useRef<HTMLDivElement | null>(null);
  useCover(`sheet:${id}`, true);

  const drag = useDragDismiss<HTMLDivElement>({
    direction: 'down',
    onDismiss,
    fraction: critical ? 0.4 : 0.25,
    flick: critical ? 0.9 : 0.5,
    scrollRef,
    onProgress: (p, animated) => {
      const el = backdrop.current;
      if (!el) return;
      el.style.transition = animated ? 'opacity .28s ease' : 'none';
      el.style.opacity = String(1 - p);
    },
  });

  const sheet = (
    <div style={{ position: 'absolute', inset: 0, zIndex, pointerEvents: 'auto' }}>
      <div
        ref={backdrop}
        className="cc-fade"
        onClick={critical ? undefined : drag.dismiss}
        style={{ position: 'absolute', inset: 0, background: 'rgba(2,3,6,.66)', touchAction: 'none' }}
      />
      <div
        ref={(el) => {
          drag.ref.current = el;
          drag.handleRef(el);
        }}
        {...drag.bind}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        className="cc-sheet"
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          ...(size === 'full' ? { top: 'calc(env(safe-area-inset-top) + 10px)' } : {}),
          maxHeight: 'calc(100% - env(safe-area-inset-top) - 10px)',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
          background: tone.bg,
          color: tone.fg,
          borderTop: `1px solid ${tone.border}`,
          borderRadius: '20px 20px 0 0',
          boxShadow: '0 -12px 40px rgba(0,0,0,.6)',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        <div
          style={{
            position: 'relative',
            flex: 'none',
            height: 44,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            touchAction: 'none',
            cursor: 'grab',
          }}
        >
          <span aria-hidden="true" style={{ width: 36, height: 5, borderRadius: 3, background: tone.grip, opacity: 0.55 }} />
          {closeLabel && (
            <button
              onClick={drag.dismiss}
              style={{
                position: 'absolute',
                right: 10,
                top: 0,
                minWidth: 44,
                minHeight: 44,
                padding: '0 12px',
                border: 'none',
                background: 'transparent',
                color: tone.grip,
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 12,
                letterSpacing: 1.5,
              }}
            >
              {closeLabel}
            </button>
          )}
        </div>
        {header && <div style={{ flex: 'none', padding: '0 18px', touchAction: 'none' }}>{header}</div>}
        <div
          ref={scrollRef}
          style={{
            flex: '1 1 auto',
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
            padding: '4px 18px 18px',
            display: 'flex',
            flexDirection: 'column',
            ...bodyStyle,
          }}
        >
          {children}
        </div>
        {footer && (
          <div
            style={{
              flex: 'none',
              padding: '12px 18px 14px',
              borderTop: `1px solid ${tone.border}`,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              touchAction: 'none',
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <SheetContext.Provider value={{ close: drag.dismiss }}>
      {layer ? createPortal(sheet, layer) : sheet}
    </SheetContext.Provider>
  );
}
